import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, NavLink } from 'react-router-dom';
import { Menu, X, MapPin, Phone, Mail, Instagram } from 'lucide-react';
import { ANNOUNCEMENT, BUSINESS } from '../data/site';
const NAV_ITEMS = [{
  to: '/',
  label: 'Home'
}, {
  to: '/programs',
  label: 'Programs'
}, {
  to: '/speed-school',
  label: 'Speed School'
}, {
  to: '/fall-2026',
  label: 'Fall 2026'
}, {
  to: '/pricing',
  label: 'Pricing'
}, {
  to: '/about',
  label: 'About'
}, {
  to: '/contact',
  label: 'Contact'
}];
export function Layout({
  children
}: {
  children: React.ReactNode;
}) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const location = useLocation();
  const isHome = location.pathname === '/';
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    setIsMobileMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);
  useEffect(() => {
    if (!isMobileMenuOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsMobileMenuOpen(false);
        menuButtonRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isMobileMenuOpen]);
  const useTransparentHeader = isHome && !isScrolled;
  const headerBg = useTransparentHeader ? 'bg-transparent' : 'bg-reach-black';
  return <div className="min-h-screen flex flex-col bg-reach-white" data-id="element-24" data-mp="1du3pw8ctsveo">
      <a href="#main-content" className="skip-link" data-id="element-25" data-mp="2k9ehah5o0cyc">
        Skip to main content
      </a>

      <header className="fixed top-0 left-0 right-0 z-50" data-id="element-26" data-mp="2p88xrcg5tpfa">
        <div className="bg-reach-red" data-id="element-27" data-mp="2p6o73xwyuqmp">
          <p className="max-w-container mx-auto px-4 md:px-8 py-2 text-center font-inter text-[11px] md:text-xs font-semibold uppercase tracking-[0.2em] text-white" data-id="element-28" data-mp="1zk3m4q156t6s">
            {ANNOUNCEMENT}
          </p>
        </div>

        <div className={`transition-colors duration-300 ${headerBg} ${isScrolled ? 'py-2 border-b border-white/10' : 'py-2 md:py-3'}`} data-id="element-29" data-mp="29ijc2pofnwep">
          <div className="max-w-container mx-auto px-4 md:px-8 flex items-center justify-between" data-id="element-30" data-mp="24h3sz35tfzyy">
            <Link to="/" className="flex items-center shrink-0" aria-label="Reach Performance home" data-id="element-31" data-mp="1grhoc06ocsjk">
              <img src="https://cdn.magicpatterns.com/uploads/esRWbJLRpNThsrPH4a2raJ/Reach-Banner-Wht.png" alt="Reach Performance" className="h-16 md:h-20 w-auto object-contain" data-id="element-32" data-mp="1bsoc0b34j7kz" />
            </Link>

            <nav className="hidden xl:flex items-center gap-4" aria-label="Primary" data-id="element-33" data-mp="y1taxkyv9ury">
              {NAV_ITEMS.map(item => <NavLink key={item.to} to={item.to} end={item.to === '/'} className={({
              isActive
            }) => `font-inter text-xs font-medium uppercase tracking-wide whitespace-nowrap transition-colors hover:text-reach-red-light ${isActive ? 'text-reach-red-light' : 'text-white'}`} data-id="element-34" data-mp="t1x65jichzbc">
                  {item.label}
                </NavLink>)}
            </nav>

            <div className="hidden xl:flex items-center shrink-0" data-id="element-35" data-mp="3nfrvtq7bo8ed">
              <Link to="/book" className="btn-primary !min-h-[40px] !px-4 !text-[11px] whitespace-nowrap" data-id="element-36" data-mp="3sfmmbsn6qvyw">
                Book Free Athlete Evaluation
              </Link>
            </div>

            <button ref={menuButtonRef} type="button" className="xl:hidden p-2 text-white" onClick={() => setIsMobileMenuOpen(open => !open)} aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'} aria-expanded={isMobileMenuOpen} aria-controls="mobile-menu" data-id="element-37" data-mp="3ikulmfyb3szw">
              {isMobileMenuOpen ? <X size={28} aria-hidden="true" data-id="element-38" data-mp="2ihxzco60p71b" /> : <Menu size={28} aria-hidden="true" data-id="element-39" data-mp="2dkemnsoyx2bh" />}
            </button>
          </div>
        </div>

        {isMobileMenuOpen && <div id="mobile-menu" className="fixed inset-0 top-0 bg-reach-black z-40 flex flex-col overflow-y-auto pt-32 px-6 pb-10 xl:hidden" data-id="element-40" data-mp="28n63ppvzwu96">
            <nav className="flex flex-col gap-1" aria-label="Mobile" data-id="element-41" data-mp="31a8tntmlvwnt">
              {NAV_ITEMS.map(item => <NavLink key={item.to} to={item.to} end={item.to === '/'} className={({
            isActive
          }) => `font-bebas text-3xl tracking-wide border-b border-white/10 py-4 ${isActive ? 'text-reach-red-light' : 'text-white'}`} data-id="element-42" data-mp="2wa4dy5tiv5yn">
                  {item.label}
                </NavLink>)}
            </nav>
            <div className="mt-8 flex flex-col gap-3" data-id="element-43" data-mp="3sg7hoe2dw4ea">
              <Link to="/book" className="btn-primary w-full" data-id="element-44" data-mp="f165baj97l4">
                Book Free Athlete Evaluation
              </Link>
              <a href={BUSINESS.smsHref} className="btn-secondary-dark w-full" data-id="element-45" data-mp="5b81yb97t2pc">
                Text {BUSINESS.phoneDisplay}
              </a>
            </div>
          </div>}
      </header>

      <main id="main-content" tabIndex={-1} className={`flex-grow focus:outline-none ${isHome ? '' : 'pt-32 md:pt-40'}`} data-id="element-46" data-mp="f58gf3qdzkaq">
        {children}
      </main>

      <footer className="bg-reach-black text-white pt-16 pb-8" data-id="element-47" data-mp="o4c30horihtz">
        <div className="max-w-container mx-auto px-4 md:px-8" data-id="element-48" data-mp="t35gia41u8u5">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12" data-id="element-49" data-mp="2pv6xn678gaz4">
            <div data-id="element-50" data-mp="2pxjpcb2slzpd">
              <img src="https://cdn.magicpatterns.com/uploads/esRWbJLRpNThsrPH4a2raJ/Reach-Banner-Wht.png" alt="Reach Performance" className="w-[200px] h-auto object-contain mb-5" data-id="element-51" data-mp="2uu0cp3264a1e" />
              <p className="font-bebas text-reach-red-light text-sm tracking-[0.2em] mb-5" data-id="element-52" data-mp="3t7ea43f7415q">
                STABILITY · STRENGTH · POWER
              </p>
              <p className="font-inter text-sm text-gray-300 leading-relaxed" data-id="element-53" data-mp="61n14wfwjdto">
                Sports performance training for high school and homeschool athletes in North Texas.
              </p>
            </div>

            <div data-id="element-54" data-mp="g02uz76bd16v">
              <h2 className="font-bebas text-xl tracking-widest text-reach-red-light mb-5" data-id="element-55" data-mp="fyi1lkvqjoal">PROGRAMS</h2>
              <ul className="space-y-3 font-inter text-sm text-gray-300" data-id="element-56" data-mp="tpo5dt61s83a">
                <li data-id="element-57" data-mp="poujt6g2v5dk">
                  <Link to="/programs" className="hover:text-white transition-colors" data-id="element-58" data-mp="pqnowhb1lhjg">
                    High School Advanced Performance
                  </Link>
                </li>
                <li data-id="element-59" data-mp="yqu0tyr3e9gi">
                  <Link to="/programs" className="hover:text-white transition-colors" data-id="element-60" data-mp="yrlvn7cv835q">
                    Homeschool Multisport
                  </Link>
                </li>
                <li data-id="element-61" data-mp="1nb76dmoehgkd">
                  <Link to="/speed-school" className="hover:text-white transition-colors" data-id="element-62" data-mp="1n9m6dhjbh5f4">
                    Speed School, September 12 and 26
                  </Link>
                </li>
                <li data-id="element-63" data-mp="1dbpwsu612ij4">
                  <Link to="/pricing" className="hover:text-white transition-colors" data-id="element-64" data-mp="1dawqfuazdv1i">
                    Private and semi-private training
                  </Link>
                </li>
                <li data-id="element-65" data-mp="14cuqzj6wbagc">
                  <Link to="/book" className="hover:text-white transition-colors" data-id="element-66" data-mp="14e5x3pntcuam">
                    Free athlete evaluation
                  </Link>
                </li>
              </ul>
            </div>

            <div data-id="element-67" data-mp="ujx6x8m99dfm">
              <h2 className="font-bebas text-xl tracking-widest text-reach-red-light mb-5" data-id="element-68" data-mp="gmqqvlsx1su3">RESOURCES</h2>
              <ul className="space-y-3 font-inter text-sm text-gray-300" data-id="element-69" data-mp="knu5k4z89itq">
                <li data-id="element-70" data-mp="kpwusmsk37xe">
                  <Link to="/about" className="hover:text-white transition-colors" data-id="element-71" data-mp="113bcwmc1di43">
                    About Coach Fernando
                  </Link>
                </li>
                <li data-id="element-72" data-mp="w2f908ropvyo">
                  <Link to="/fall-2026" className="hover:text-white transition-colors" data-id="element-73" data-mp="r5f7mb5s5hdw">
                    Fall 2026 schedule
                  </Link>
                </li>
                <li data-id="element-74" data-mp="m8ez7tlgk2jm">
                  <Link to="/pricing" className="hover:text-white transition-colors" data-id="element-75" data-mp="i4qr99swz4xu">
                    Training rates
                  </Link>
                </li>
                <li data-id="element-76" data-mp="dcphbuj7xqaz">
                  <Link to="/contact" className="hover:text-white transition-colors" data-id="element-77" data-mp="dc5x2763ipts">
                    Contact and directions
                  </Link>
                </li>
              </ul>
            </div>

            <div data-id="element-78" data-mp="3um4bfpdzaczv">
              <h2 className="font-bebas text-xl tracking-widest text-reach-red-light mb-5" data-id="element-79" data-mp="3umnjmedttn0p">GET IN TOUCH</h2>
              <ul className="space-y-4 font-inter text-sm text-gray-300" data-id="element-80" data-mp="3qkaglxqgs37m">
                <li className="flex items-start gap-3" data-id="element-81" data-mp="262rtfh2hacwq">
                  <MapPin size={18} className="shrink-0 mt-0.5 text-reach-red-light" aria-hidden="true" data-id="element-82" data-mp="261ymog3irnel" />
                  <a href={BUSINESS.mapsUrl} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors leading-relaxed" data-id="element-83" data-mp="2a2a47k0l57lu">
                    {BUSINESS.venue}
                    <br data-id="element-84" data-mp="1mg3n8896ohjh" />
                    {BUSINESS.street}
                    <br data-id="element-85" data-mp="1rcu06mpoyx0o" />
                    {BUSINESS.city}, {BUSINESS.state} {BUSINESS.zip}
                  </a>
                </li>
                <li className="flex items-center gap-3" data-id="element-86" data-mp="1wb3yv7amkfr1">
                  <Phone size={18} className="shrink-0 text-reach-red-light" aria-hidden="true" data-id="element-87" data-mp="213x37u26lm2l" />
                  <a href={BUSINESS.phoneHref} className="hover:text-white transition-colors" data-id="element-88" data-mp="13oj7i6u5g3nh">
                    {BUSINESS.phoneDisplay}
                  </a>
                </li>
                <li className="flex items-center gap-3" data-id="element-89" data-mp="itnnfdcpx7pd">
                  <Mail size={18} className="shrink-0 text-reach-red-light" aria-hidden="true" data-id="element-90" data-mp="it42dnrru374" />
                  <a href={`mailto:${BUSINESS.email}`} className="hover:text-white transition-colors break-all" data-id="element-91" data-mp="nr5okhkhuimt">
                    {BUSINESS.email}
                  </a>
                </li>
                <li className="flex items-center gap-3" data-id="element-92" data-mp="3ec9dyye5lj">
                  <Instagram size={18} className="shrink-0 text-reach-red-light" aria-hidden="true" data-id="element-93" data-mp="4z4gwxspcg8" />
                  <a href={BUSINESS.instagram} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors" data-id="element-94" data-mp="52imm7xm69dg">
                    @reachperformancehq
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/10 pt-6 flex flex-col md:flex-row justify-between items-center gap-3" data-id="element-95" data-mp="3ig3u9ekrn2xn">
            <p className="font-inter text-xs text-gray-400" data-id="element-96" data-mp="1zlhgl3iwsno8">
              © 2026 Reach Performance LLC. All rights reserved.
            </p>
            <p className="font-bebas text-xs tracking-[0.25em] text-gray-400" data-id="element-97" data-mp="1ql2pton6egdc">
              STABILITY · STRENGTH · POWER
            </p>
          </div>
        </div>
      </footer>
    </div>;
}